import { useEffect, useMemo, useRef, useState } from 'react';

import { getUserData } from '../constants/global';
import { sendSupportHeartbeat } from '../lib/support-api';
import { type SupportConnectionStatus, SupportLiveClient } from '../lib/support-signalr';

/**
 * Heartbeat cadence. Comfortably inside the desk's 90-second `appOpen` window so a single
 * dropped beat does not demote a working agent to "app seen a minute ago".
 */
const HEARTBEAT_MS = 45_000;

/**
 * What is shared between every conversation this agent has open.
 *
 * <p>One socket, one presence heartbeat, one `navigator.onLine` reading, one identity. The
 * conversation engines (`useChatThread`) take this as an argument rather than owning any of it,
 * so a second conversation never opens a second hub connection.</p>
 */
export interface ISupportSession {
	/** The signed-in agent's core user id. 0 when signed out. */
	me: number;
	/** Null until the layout has mounted it. */
	live: SupportLiveClient | null;
	liveStatus: SupportConnectionStatus;
	online: boolean;
}

/**
 * Session-wide support state, owned by the Dashboard layout.
 *
 * <p>Mounted from <c>components/layout/index.tsx</c>, NOT from a panel. Right-section panels
 * unmount when inactive, so a panel-owned socket would tear down the moment an agent closes the
 * chat to go upload a result sheet — taking an in-flight send with it and leaving the FAB badge
 * with nothing to count.</p>
 */
export const useSupportSession = (enabled = true): ISupportSession => {
	// Disabled (an Independent Supporter, whom api.vesr's hub refuses) reads as signed out: no socket,
	// no heartbeat, and every chat engine keyed on `me` stays idle.
	const me = enabled ? Number(getUserData()?.user?.UserId ?? 0) : 0;

	const [live, setLive] = useState<SupportLiveClient | null>(null);
	// The heartbeat reads the client through a ref so its effect keys on `me` alone: with
	// `live` in the deps, `setLive(client)` re-ran it — a duplicate beat on mount and a reset
	// of the 45 s interval.
	const liveRef = useRef<SupportLiveClient | null>(null);
	const [liveStatus, setLiveStatus] = useState<SupportConnectionStatus>('disconnected');
	const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));

	useEffect(() => {
		const goOnline = () => setOnline(true);
		const goOffline = () => setOnline(false);
		window.addEventListener('online', goOnline);
		window.addEventListener('offline', goOffline);
		return () => {
			window.removeEventListener('online', goOnline);
			window.removeEventListener('offline', goOffline);
		};
	}, []);

	/**
	 * Live connection.
	 *
	 * Owned here, not by a panel: the FAB badge must keep updating while the chat is closed,
	 * and an in-flight send must survive an agent closing the chat to upload a result sheet.
	 */
	useEffect(() => {
		if (!me) return;

		const client = new SupportLiveClient();
		liveRef.current = client;
		setLive(client);
		void client.connect(setLiveStatus);

		return () => {
			liveRef.current = null;
			setLive(null);
			void client.disconnect();
		};
	}, [me]);

	/**
	 * Tell the desk this app is alive.
	 *
	 * <p>Only while the tab is VISIBLE. That is the whole point: a backgrounded app keeps its
	 * socket, so heartbeating from a hidden tab would report a phone in a pocket as reachable
	 * and the desk would wait for a reply nobody is coming to write.</p>
	 *
	 * <p>Every 45s against the desk's 90s `appOpen` window, so one dropped beat does not flip
	 * a working agent to "seen a minute ago". Tries the socket first and falls back to HTTP,
	 * because the socket is exactly what is unreliable in the conditions where reachability
	 * matters most.</p>
	 */
	useEffect(() => {
		if (!me) return;

		const beat = () => {
			if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
			if (typeof navigator !== 'undefined' && !navigator.onLine) return;

			// Fall back on DELIVERY, not on the client merely existing. A socket that was
			// constructed but never connected — a network that blocks WebSockets, which is
			// common at a collation centre — would otherwise swallow every beat silently, and
			// the agents hardest to reach would be the ones reported as least reachable.
			void (async () => {
				const delivered = await liveRef.current?.heartbeat();
				if (!delivered) await sendSupportHeartbeat();
			})();
		};

		beat();
		const id = window.setInterval(beat, HEARTBEAT_MS);
		// Returning to the app should register immediately, not up to 45s later.
		document.addEventListener('visibilitychange', beat);

		return () => {
			window.clearInterval(id);
			document.removeEventListener('visibilitychange', beat);
		};
	}, [me]);

	// Memoised: this object is a prop of every hosted engine and a context value; a fresh
	// object per render would re-render each host on every layout render.
	return useMemo(() => ({ me, live, liveStatus, online }), [me, live, liveStatus, online]);
};
